<template>
  <modal-window :loading="loading" v-on:close="$emit('closeProductEditor')">
    <template slot="header">
      <p class="modal-card-title">{{ header }}</p>
      <button class="delete" v-on:click="$emit('closeProductEditor')" aria-label="close"></button>
    </template>
    <template slot="body">
      <div class="field">
        <b-tooltip label="Name of the product" position="is-right">
          <label class="label">Name</label>
        </b-tooltip>
        <b-field v-bind:type="{'is-danger':  newProductName.length < 2 && saveClicked}"
                 v-bind:message="{'You should specify product name!' : newProductName.length < 2 && saveClicked }">
          <b-input v-model.trim="newProductName" maxlength="100" placeholder="FastReport.Jar"></b-input>
        </b-field>
      </div>

      <div class="field">
        <b-tooltip label="Brief description of the product" position="is-right">
          <label class="label">Description</label>
        </b-tooltip>
        <b-field>
          <b-input type="textarea"
                   v-model.trim="newProductDescription"
                   maxlength="500"
                   placeholder="Java report generator"
                   spellcheck="true"></b-input>
        </b-field>
      </div>

      <div v-if="!inherited" class="field">
        <b-tooltip label="Web page of the product" position="is-right">
          <label class="label">Product url</label>
        </b-tooltip>
        <b-field>
          <b-input v-model.trim="newProductUrl"
                   placeholder="https://somefarinthegalaxy.com/product"></b-input>
        </b-field>
      </div>

      <div v-if="!inherited" class="field">
        <b-tooltip label="Web page of the product repository" position="is-right">
          <label class="label">Repository url</label>
        </b-tooltip>
        <b-field>
          <b-input v-model.trim="newProductRepoUrl"
                   placeholder="https://somefarinthegalaxy.com/repo"></b-input>
        </b-field>
      </div>

      <div v-if="!inherited" class="field">
        <b-tooltip label="Category of the product regarding used technologies" position="is-right">
          <label class="label">Tag (.Net/VCL)</label>
        </b-tooltip>

        <b-field>
          <b-input v-model.trim="newProductTag" maxlength="20" placeholder="Java"></b-input>
        </b-field>
      </div>

      <div v-if="!inherited" class="field">
        <b-tooltip label="Average building time of the product" position="is-right">
          <label class="label">Average build time, seconds</label>
        </b-tooltip>
        <b-field>
          <b-input type="number" v-model.number.trim="newProductAvgBuildTime" placeholder="10000"></b-input>
        </b-field>
      </div>

      <div v-if="!inherited" class="field">
        <b-tooltip label="Type product subsystems, separate by comma, please!" position="is-right">
          <label class="label">Product parts</label>
        </b-tooltip>

        <b-field v-bind:type="{'is-danger':  productParts.length < 2 && saveClicked}"
                 v-bind:message="{'You should specify product parts!' : productParts.length < 2 && saveClicked }">
          <b-taginput placeholder="Engine, Exports, .Net Core"
                      v-model="productParts"
                      ellipsis
                      icon="label"
                      :data="filteredProductParts"
                      :allow-new="true"
                      autocomplete
                      @typing="getFilteredParts"
                      :before-adding="beforeTagAdding"></b-taginput>
        </b-field>
      </div>

      <div class="field" v-if="productId <= 0 && ! inherited">
        <label class="label">Initial version</label>
        <div class="field is-grouped tooltip" data-tooltip="First version of the product">
          <p class="control">
            <label class="label">Major</label>
            <input class="input"
                   type="number"
                   min="0"
                   step="1"
                   pattern="\d*"
                   v-model.number.trim="newProductMajor"
                   v-bind:class="{ 'is-danger':  (newProductMajor < 0 || !isInteger(newProductMajor)) && saveClicked}">
          </p>
          <p class="control">
            <label class="label">Minor</label>
            <input class="input"
                   type="number"
                   min="0"
                   step="1"
                   pattern="\d*"
                   v-model.number.trim="newProductMinor"
                   v-bind:class="{ 'is-danger':  (newProductMinor < 0 || !isInteger(newProductMinor)) && saveClicked}">
          </p>
          <p class="control">
            <label class="label">Build</label>
            <input class="input"
                   type="number"
                   min="0"
                   step="1"
                   pattern="\d*"
                   v-model.number.trim="newProductBuild"
                   v-bind:class="{ 'is-danger':  (newProductBuild < 0 || !isInteger(newProductBuild)) && saveClicked}">
          </p>
        </div>
        <p v-if="(newProductMajor < 0 || newProductMinor < 0 || newProductBuild < 0) && saveClicked"
           class="help is-danger">You should specify not negative product version!</p>
        <p v-if="(!isInteger(newProductMajor) || !isInteger(newProductMinor) || !isInteger(newProductBuild)) && saveClicked"
           class="help is-danger">Product version should be integer!</p>
      </div>

      <!-- errors -->
      <br>
      <div class="has-text-left has-text-danger" v-if="errors.length">
        <b class="label is-danger">Please fix this error:</b>
        <ul>
          <li class="is-danger" v-for="(error,index) in errors" :key="index">{{ error }}</li>
        </ul>
      </div>
      <!-- end errors -->
    </template>
    <template slot="footer">
      <button class="button is-success" v-on:click="submit()">Save</button>
      <button class="button" v-on:click="$emit('closeProductEditor')">Cancel</button>
      <b-tooltip label="Create a new product based on this one" position="is-right">
        <button v-if="(this.productId != 0)" class="button is-info" v-on:click="createInheritedProduct()">Copy</button>
      </b-tooltip>
    </template>
  </modal-window>
</template>

<script>
  import ModalWindow from "./ModalWindow";
  //import this.HTTP from "./Http";

  export default {
    name: "product-editor",
    components: { ModalWindow },
    props: {
      productId: Number,
      header: String
    },
    data() {
      return {
        inherited: false,
        baseProductId: 0,
        newProductName: "",
        newProductDescription: "",
        newProductUrl: "",
        newProductRepoUrl: "",
        newProductTag: "",
        newProductAvgBuildTime: 0,
        newProductMajor: 2019,
        newProductMinor: 1,
        newProductBuild: 0,

        saveClicked: false,
        recordProcessing: false, //should prevent record duplicate creating
        errors: [],

        productParts: [],
        allProductParts: [],
        filteredProductParts: [],
        loading: false
      };
    },
    computed: {
      productPartsArray: function () {
        if (this.productParts != null && this.productParts.length > 0) {
          let partsObj;
          let partsArray = [];
          this.productParts.forEach(element => {
            if (element.trim().length > 0) {
              partsObj = {
                productId: this.productId,
                group: {
                  name: element.trim()
                }
              };
              partsArray.push(partsObj);
            }
          });
          return partsArray;
        } else return null;
      }
    },
    methods: {
      createInheritedProduct() {
        this.HTTP.post('Products/' + this.productId + '/inherit', {
          name: this.newProductName,
          description: this.newProductDescription,
          productChangeGroups: this.productPartsArray,
          tag: this.newProductTag,
          avgBuildTime: this.newProductAvgBuildTime,
          productUrl: this.newProductUrl,
          repositoryUrl: this.newProductRepoUrl
        }).then(response => {
          this.recordProcessing = false;
          this.$emit("closeProductEditor");
        })
          .catch(e => {
            this.recordProcessing = false;
            this.errors = e.response.data.description;
          });
      },
      createProduct() {
        this.HTTP.post(`products`, {
          name: this.newProductName,
          description: this.newProductDescription,
          productUrl: this.newProductUrl,
          repositoryUrl: this.newProductRepoUrl,
          tag: this.newProductTag,
          avgBuildTime: this.newProductAvgBuildTime,
          versions: [
            {
              name: "Initial Version",
              major: this.newProductMajor,
              minor: this.newProductMinor,
              build: this.newProductBuild,
              state: "Success"
            }
          ],
          productChangeGroups: this.productPartsArray
        })
          .then(response => {
            this.recordProcessing = false;
            this.$emit("closeProductEditor");
          })
          .catch(e => {
            this.recordProcessing = false;
            this.errors = e.response.data.description;
          });
      },
      editProduct(productId) {
        this.HTTP.put(`products/` + productId, {
          id: productId,
          name: this.newProductName,
          description: this.newProductDescription,
          productUrl: this.newProductUrl,
          repositoryUrl: this.newProductRepoUrl,
          tag: this.newProductTag,
          avgBuildTime: this.newProductAvgBuildTime,
          productChangeGroups: this.productPartsArray
        })
          .then(response => {
            this.recordProcessing = false;
            this.$emit("closeProductEditor");
          })
          .catch(e => {
            this.recordProcessing = false;
            this.errors = e.response.data.description;
          });
      },

      getProduct(id) {
        this.loading = true;
        this.HTTP.get("products/" + id)
          .then(response => {
            this.newProductName = response.data.name;
            this.newProductDescription = response.data.description;
            this.newProductUrl = response.data.productUrl;
            this.newProductRepoUrl = response.data.repositoryUrl;
            this.newProductTag = response.data.tag;
            this.newProductAvgBuildTime = response.data.avgBuildTime;

            if (
              response.data.productChangeGroups != null &&
              response.data.productChangeGroups.length > 0
            ) {
              this.productParts = [];
              response.data.productChangeGroups.forEach(element => {
                this.productParts.push(element.group.name);
              });
              // this.productParts = this.productParts.substring(
              //   0,
              //   this.productParts.length - 2
              // );
            }
            this.loading = false;
          })
          .catch(e => {
            this.loading = false;
            console.log(e);
          });
      },
      submit() {
        console.log("recordProcessing - " + this.recordProcessing);
        if (!this.recordProcessing) {
          this.saveClicked = true;
          if (
            this.newProductMajor >= 0 &&
            this.newProductMinor >= 0 &&
            this.newProductBuild >= 0 &&
            this.isInteger(this.newProductMajor) &&
            this.isInteger(this.newProductMinor) &&
            this.isInteger(this.newProductBuild)
          ) {
            if (this.newProductName.length >= 2 && this.productParts.length > 0) {
              this.recordProcessing = true;
              if (this.productId <= 0) this.createProduct();
              else this.editProduct(this.productId);
            }
          }
        }
      },
      getProductParts() {
        this.HTTP.get("ChangeGroups/1/9999")
          .then(response => {
            this.allProductParts = response.data.entities.map(p => p.name);
          })
          .catch(e => {
            console.log(e);
          });
      },
      getFilteredParts(text) {
        this.filteredProductParts = this.allProductParts.filter(option => {
          return option.toLowerCase().indexOf(text.toLowerCase()) >= 0;
        });
      },
      isInteger(num) {
        return (num ^ 0) === num;
      },
      beforeTagAdding(tag) {
        for (let k of this.productParts) {
          if (tag.toLowerCase() == k.toLowerCase()) return false;
        }
        return true;
      }

      // keyDownListener(evt) {
      //   if (evt.keyCode === 27) this.$emit("closeProductEditor");
      //   else if (evt.keyCode === 13) this.submit();
      // }
    },

    created() {
      document.addEventListener("keydown", this.keyDownListener);
      this.getProductParts();
      if (this.productId > 0) {
        this.getProduct(this.productId);
      }
    },
    destroyed: function () {
      document.removeEventListener("keydown", this.keyDownListener);
    }
  };
</script>



// WEBPACK FOOTER //
// src/components/ProductEditor.Vue